import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { selectCustomStyles } from '../../../components/common/shared/select.styled';
import { useAccessToken } from '../../../components/common/hooks/useAccessToken';
import { showLoader, hideLoader } from '../../../components/store/common/slice';
import { changePartnersList } from '../../../components/store/admin/slice';
import { StyledDropdownWrapper } from './PartnersAndBuildingsDropdowns.styled';

const PartnersAndBuildingsDropdowns = ({ disabled, partnerValue, buildingsValue, onPartnerChange, onBuildingsChange, isMultiPartners = true }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const client = useAccessToken();
    const partnersList = useSelector(state => state.admin.partnersList);
    const currentPartnerShared = useSelector(state => state.admin.currentPartnerShared);

    const [currentPartner, setCurrentPartner] = useState(partnerValue || []);
    const [currentBuildings, setCurrentBuildings] = useState(buildingsValue || []);
    const [partnersOptions, setPartnersOptions] = useState([]);
    const [buildingsOptions, setBuildingsOptions] = useState([]);
    const [disabledBuildingsDropdown, setDisabledBuildingsDropdown] = useState(true);

    useEffect(() => {
        if (!client) return;
        (async function () {
            dispatch(showLoader());
            if (partnersList.length > 0) {
                setPartnersOptions(partnersList)
            } else {
                try {
                    const { partners } = await client.get('/operations/data/partners');
                    const optionsArr = (partners ?? []).map(item => ({
                        value: item.partnerID,
                        label: `${item.partnerName} - ${item.partnerID} - ${item.externalID}`,
                        disabled: item.disabled
                    }));
                    setPartnersOptions(optionsArr);
                    dispatch(changePartnersList(optionsArr));
                } catch (e) {
                    return;
                }
            }

            if (currentPartnerShared?.value) setCurrentPartner(currentPartnerShared);
            dispatch(hideLoader());
        })();
    }, [client]);

    useEffect(() => {
        if (partnerValue) setCurrentPartner(partnerValue)
    }, [partnerValue]);

    useEffect(() => {
        if (buildingsValue) setCurrentBuildings(buildingsValue)
    }, [buildingsValue]);

    const getBuildingsList = async (partnerId) => {
        dispatch(showLoader());
        const { buildings } = await client.get(`/operations/data/partners/${partnerId}/buildings`);
        const optionsArr = (buildings ?? []).map(item => ({
            value: item.PtoPId,
            label: `${item.Title} - ${item.PtoPId}`,
            disabled: item.disabled
        }));
        setBuildingsOptions(optionsArr);
        dispatch(hideLoader());
    };

    useEffect(() => {
        if (currentPartner.length === 1) {
            setDisabledBuildingsDropdown(false);
            getBuildingsList(currentPartner[0].value);
        } else {
            setDisabledBuildingsDropdown(true);
        }
    }, [currentPartner]);

    const onPartnerDropdownChange = e => {
        onPartnerChange(e);
        setCurrentPartner(e);
    };

    const onBuildingsDropdownChange = e => {
        onBuildingsChange(e);
        setCurrentBuildings(e);
    };

    return (
        <>
            <StyledDropdownWrapper className='dropdown'>
                <label className="label">{t('autoCampaign.actions.labels.partner')}</label>
                <Select
                    styles={selectCustomStyles}
                    options={partnersOptions}
                    value={currentPartner}
                    onChange={onPartnerDropdownChange}
                    name="partnerName"
                    isMulti={isMultiPartners}
                    isOptionDisabled={option => option.disabled}
                    isDisabled={disabled}
                />
            </StyledDropdownWrapper>

            <StyledDropdownWrapper className='dropdown'>
                <label className="label">{t('autoCampaign.actions.labels.buildings')}</label>
                <Select
                    styles={selectCustomStyles}
                    options={buildingsOptions}
                    value={currentBuildings}
                    onChange={onBuildingsDropdownChange}
                    name="partnerBuildings"
                    isMulti
                    isDisabled={disabledBuildingsDropdown || disabled}
                    isOptionDisabled={option => option.disabled}
                />
            </StyledDropdownWrapper>
        </>
    )
};

export default PartnersAndBuildingsDropdowns;
